import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ApiBaseUrlContext } from '../App';
import Select from 'react-select';

const EditRights = () => {
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [permissions, setPermissions] = useState([]);  // Permissions array
  const [selectedRole, setSelectedRole] = useState(null);
  const [privileges, setPrivileges] = useState({
    can_edit: false,
    can_delete: false,
    can_view: false,
    can_get_machine: false,
  });
  const [rolePageAccess, setRolePageAccess] = useState([]);
  // Fetch roles, pages, and permissions on mount
  useEffect(() => {
    const fetchRolesPagesAndPermissions = async () => {
      try {
        const [rolesResponse, pagesResponse, permissionsResponse] = await Promise.all([
          axios.get(`${apiBaseUrl}/mnt/api/roles/privileges`),
          axios.get(`${apiBaseUrl}/mnt/api/settings/pages`),
          axios.get(`${apiBaseUrl}/mnt/api/permissions`)
        ]);

        setRoles(rolesResponse.data);
        setPages(pagesResponse.data);
        setPermissions(permissionsResponse.data || []);  // Ensure permissions is an array
      } catch (error) {
        console.error('Error fetching roles, pages, or permissions:', error);
      }
    };

    fetchRolesPagesAndPermissions();
  }, [apiBaseUrl]);

  // Handle role selection
  const handleRoleChange = (selectedOption) => {
    const selectedRoleId = selectedOption.value;
    const role = roles.find((role) => role.role_id === selectedRoleId);

    if (role) {
      setSelectedRole(selectedOption);
      setPrivileges({
        can_edit: role.can_edit !== null ? role.can_edit : false,
        can_delete: role.can_delete !== null ? role.can_delete : false,
        can_view: role.can_view !== null ? role.can_view : false,
        can_get_machine: role.can_get_machine !== null ? role.can_get_machine : false,
      });
      setRolePageAccess(role.pages ? role.pages.split(',').map(Number) : []);
    } else {
      console.error('Role not found or undefined');
    }
  };

  // Handle privilege checkbox changes
  const handlePrivilegeChange = (e) => {
    const { name, checked } = e.target;
    setPrivileges((prev) => ({ ...prev, [name]: checked }));
  };

  // Handle page access checkbox changes
  const handlePageAccessChange = (pageId, checked) => {
    setRolePageAccess((prevAccess) =>
      checked ? [...prevAccess, pageId] : prevAccess.filter((id) => id !== pageId)
    );
  };

  // Map permission names to their IDs
  const getPermissionIdByName = (name) => {
    if (!Array.isArray(permissions)) {
      console.error("Permissions is not an array or is undefined:", permissions);
      return null;
    }

    const permission = permissions.find((perm) => perm.permission_name === name);
    return permission ? permission.permission_id : null;
  };

  // Handle privilege and page access update submission
  const handleSavePrivileges = async () => {
    if (selectedRole) {
      // Map each privilege to the corresponding permission_id
      const editPermissionId = getPermissionIdByName('edit');
      const deletePermissionId = getPermissionIdByName('delete');
      const viewPermissionId = getPermissionIdByName('view');
      const getMachine = getPermissionIdByName('getMachine');

      const payload = {
        privileges,
        pages: rolePageAccess,
        resource_id: 1,  // Assign the resource (page) ID appropriately
        edit_permission_id: editPermissionId,  // Include permission_id for edit
        delete_permission_id: deletePermissionId,  // Include permission_id for delete
        view_permission_id: viewPermissionId,  // Include permission_id for view
        get_machine_id: getMachine  // Include permission_id for view
      };
      
      console.log('Payload being sent to server:', payload);
      
      try {
        await axios.put(`${apiBaseUrl}/mnt/api/roles/privileges/${selectedRole.value}`, payload);
        alert(t('settingsPage.privilegesUpdatedSuccess'));
      } catch (error) {
        console.error('Error updating role privileges:', error);
        alert(t('settingsPage.privilegesUpdatedError'));
      }
      window.location.reload();
    }
  };

  const roleOptions = roles.map((role) => ({
    value: role.role_id,
    label: t(`roles.${role.role_name}`),
  }));

  return (

    <>

    <div className="form-section">

      <div className="entry-item">
        <label htmlFor="role-select" className="entry-label">{t('settingsPage.selectRole')}</label>
        <Select
          id="role-select"
          options={roleOptions}
          className="entry-select"
          classNamePrefix="entry-select"
          onChange={handleRoleChange}
          value={roleOptions.find((option) => option.value === selectedRole?.value)}
        />
      </div>

      {selectedRole && (

        <>

          <div className="form-section--inner-divider"></div>

          <div className="entry-item">
            <label className="entry-label">{t('settingsPage.editPrivilegesFor')}</label>
            <div className="entry-item__input-and-label">
              <input
                type="checkbox"
                className="entry-control"
                id="can_edit"
                name="can_edit"
                checked={privileges.can_edit}
                onChange={handlePrivilegeChange}
              />
              <label htmlFor="can_edit">
                {t('settingsPage.canEdit')}
              </label>
            </div>

            <div className="entry-item__input-and-label">
              <input
                type="checkbox"
                className="entry-control"
                id="can_delete"
                name="can_delete"
                checked={privileges.can_delete}
                onChange={handlePrivilegeChange}
              />
              <label htmlFor="can_delete">
                {t('settingsPage.canDelete')}
              </label>
            </div>

            <div className="entry-item__input-and-label">
              <input
                type="checkbox"
                className="entry-control"
                id="can_view"
                name="can_view"
                checked={privileges.can_view}
                onChange={handlePrivilegeChange}
              />
              <label htmlFor="can_view">
                {t('settingsPage.canView')}
              </label>
            </div>
            <div className="entry-item__input-and-label">
              <input
                type="checkbox"
                className="entry-control"
                id="can_get_machine"
                name="can_get_machine"
                checked={privileges.can_get_machine}
                onChange={handlePrivilegeChange}
              />
              <label htmlFor="can_get_machine">
                {t('settingsPage.can_get_machine')}
              </label>
            </div>

          </div>

          <div className="form-section--inner-divider"></div>

          <div className="entry-item">

            <label className="entry-label">{t('settingsPage.pageAccess')}</label>

            {pages.map((page) => (
               <div className="entry-item__input-and-label" key={page.page_id}>
                <input
                  type="checkbox"
                  className="entry-control"
                  id={`page_${page.page_id}`}
                  checked={rolePageAccess.includes(page.page_id)}
                  onChange={(e) => handlePageAccessChange(page.page_id, e.target.checked)}
                />
                <label htmlFor={`page_${page.page_id}`}>
                  {t(`pages.${page.page_name}`)}
                </label>
              </div>
            ))}

          </div>

        </>
      )}
    </div>

    <button className="button--blue" onClick={handleSavePrivileges}>
      {t('settingsPage.save')}
    </button>

    </>

  );
};

export default EditRights;
